/**
 * Conversion des rôles historiques v1 en permissions v2 (bascule « full v2 » du 19/09/2026).
 *
 * Neuf rôles créés par les seeders de mars 2026 ne portent que des permissions v1
 * (`role_permissions.resource` sans point, `permission_type` ∈ {`read`, `edit`}). Le back-office
 * n'affiche plus que la v2 : ces rôles y apparaissent vides et, au passage du flag `permission-v2`,
 * les vingt comptes qui leur sont affectés perdraient tout accès. Cette table dit, pour chaque
 * couple v1 `<ressource>:<read|edit>`, les tuples v2 `[action, ressource]` qu'il vaut.
 *
 * ## Principe de continuité
 *
 * Un compte qui avait la fonction en v1 la garde en v2, ni plus ni moins :
 * - `read` v1 → les actions `read` et `export` des ressources v2 du module correspondant ;
 * - `edit` v1 → **toutes** les actions de ces ressources (`add`, `edit`, `delete`, `import` et les
 *   actions spéciales), `read` comprise — un éditeur v1 pouvait tout faire dans son module.
 *
 * ## Correspondance des modules et cas tranchés
 *
 * La correspondance a été établie en lisant, pour chaque écran, la garde v1 qu'il portait avant la
 * bascule (`staticData.permission` du back-office et `ability.cannot('<action>', '<ressource v1>')`
 * des contrôleurs, encore en place pour le régime hérité) contre la garde v2 d'aujourd'hui :
 *
 * - `stores` couvrait aussi le module `network` (tableau de bord réseau) **et** la configuration
 *   des caméras : les 13 routes `cameras.cameras.config` sont gardées
 *   `ability.cannot('read'|'edit', 'stores')` (`app/Controllers/Http/stores/camera-configs/*`) et
 *   l'écran `/cameras/config` déclarait `['read', 'stores']`. Il ne couvre PAS
 *   `stores.store-tasks.task` (écran « Store App · tâches », postérieur à la v1).
 * - `cameras` = le flux (`cameras.cameras.stream`, action spéciale `start-intervention` comprise)
 *   et l'écran Pré-autorisations : ses quatre routes sont gardées
 *   `ability.cannot('read'|'edit', 'cameras')` (`app/Controllers/Http/openingSessions/*`) et
 *   l'écran déclarait `['read', 'cameras']`. `camera-views` ne vaut que la composition de vues
 *   personnalisées du même flux, sans `start-intervention`.
 * - `hr-planning` couvrait TOUT le module `hr` (planning, réglages, tableau de bord RH, personnel
 *   avec toutes ses ressources de champs `hr.staff.*`, pointage, absences), le suivi des SMS et le
 *   référentiel des agences d'intérim. Les permissions RH converties portent `population = null`,
 *   c'est-à-dire tous les postes et tous les types de contrat.
 * - `work-concil` couvrait le partenariat CE et l'export de son historique comptable
 *   (`accounting.accounting.works-council-history`), qui reste hors du module `accounting`.
 * - `carts` = le panier et la vérification visuelle ; l'outillage de test CPI
 *   (`carts.cpi-tests.test`) n'a pas d'équivalent v1.
 * - `cart-control` : `read` vaut la consultation du direct (`live-control`), `edit` y ajoute la
 *   prise de main sur la caisse (`enter-support-mode`).
 * - `roles` vaut l'administration des rôles et l'attribution d'un rôle depuis une fiche de compte
 *   (`assign-bo-role users.bo-users.user`) : cette action n'est PAS donnée par `users:edit`, qui
 *   reste l'édition des comptes eux-mêmes.
 * - `users` couvre les trois sous-onglets du module (utilisateurs de l'app, défauts de paiement,
 *   comptes du back-office et leurs clés API). `edit` inclut `reset-password`,
 *   `edit-legacy-rights` et `issue-for-others` : un éditeur v1 des comptes pouvait déjà tout faire.
 * - `prices` a disparu de la taxonomie v1 (module « Gestion des prix » retiré du produit) mais
 *   subsiste dans les données de production : `read` vaut la lecture de la fiche produit et `edit`
 *   la seule action `edit-prices`, volontairement exclue de `products:edit`.
 * - `real-estate` couvre tout le module foncier. Quand la permission v1 porte le périmètre
 *   `real-estate-restricted`, seuls les tuples déclarés dans `RBAC_V2_SCOPED_PERMISSIONS` le
 *   reçoivent (cf. `legacyV1ScopeForV2Tuple`) ; les autres restent globaux.
 * - `visio-reviews` vaut tout le module `visio`, `promos` tout le module `promo`.
 *
 * Les modules v2 qui n'ont aucun équivalent v1 (`mailing-lists`, `feature-flags`, `previsions`,
 * `project-tracking`, `supply`, `staging-tools`, `admin-tools`, `ai`, plus
 * `stores.store-tasks.task` et `carts.cpi-tests.test`) ne sont attribués qu'au rôle
 * `Super Admin`, traité comme l'équivalent du rôle `Full Access` du seed : il reçoit la totalité
 * de `rbacV2Abilities`, sans périmètre.
 */

import {
  RBAC_V2_SCOPED_PERMISSIONS,
  rbacV2Abilities,
  type RbacV2Action,
  type RbacV2Resource,
  type RbacV2Scope,
} from './rbac.v2.js'

/** Nom du rôle historique qui portait toute la matrice v1 et reçoit toute la matrice v2. */
export const LEGACY_FULL_ACCESS_ROLE_NAME = 'Super Admin' as const

/**
 * Ressources v1 rencontrées dans `role_permissions` en production. `prices` ne fait plus partie de
 * `rbacResourceDetails` (module retiré) mais ses lignes existent encore en base : la conversion
 * doit les connaître.
 */
export const LEGACY_V1_RESOURCES = [
  'dashboard',
  'today',
  'carts',
  'categories',
  'products',
  'users',
  'promos',
  'work-concil',
  'notifications',
  'stores',
  'accounting',
  'quality',
  'cart-control',
  'daily-sales',
  'sellers',
  'cameras',
  'camera-views',
  'sensors',
  'hr-planning',
  'real-estate',
  'roles',
  'platform-stocks',
  'visio-reviews',
  'prices',
] as const

export type LegacyV1Resource = (typeof LEGACY_V1_RESOURCES)[number]

/** Couple v1 tel qu'il est stocké : `<resource>:<permission_type>`. */
export type LegacyV1PermissionKey = `${LegacyV1Resource}:${'read' | 'edit'}`

/** Tuple v2 produit par la conversion. */
export type LegacyV2Tuple = readonly [RbacV2Action, RbacV2Resource]

/**
 * TABLE DE CONVERSION — source unique de la migration et des tests.
 * Une entrée par couple v1 existant (les ressources v1 en lecture seule — `dashboard`, `today`,
 * `carts` — n'ont pas de couple `:edit`).
 */
export const LEGACY_V1_TO_V2_GRANTS = {
  'dashboard:read': [
    ['read', 'dashboard.dashboard.global-indicators'],
    ['export', 'dashboard.dashboard.global-indicators'],
  ],
  'today:read': [['read', 'today.today.daily-tracking']],
  'carts:read': [
    ['read', 'carts.carts.cart'],
    ['export', 'carts.carts.cart'],
    ['read', 'carts.vision-checks.check'],
  ],
  'categories:read': [
    ['read', 'categories.categories.standard'],
    ['read', 'categories.internal-categories.internal'],
    ['export', 'categories.internal-categories.internal'],
    ['read', 'categories.reappro.category'],
    ['read', 'categories.reappro.config'],
    ['export', 'categories.reappro.config'],
    ['export', 'categories.reappro.simulation'],
    ['read', 'categories.reappro.adjustment'],
    ['read', 'categories.reappro.algorithm'],
    ['read', 'categories.brands.brand'],
  ],
  'categories:edit': [
    ['read', 'categories.categories.standard'],
    ['add', 'categories.categories.standard'],
    ['edit', 'categories.categories.standard'],
    ['delete', 'categories.categories.standard'],
    ['read', 'categories.internal-categories.internal'],
    ['add', 'categories.internal-categories.internal'],
    ['edit', 'categories.internal-categories.internal'],
    ['delete', 'categories.internal-categories.internal'],
    ['export', 'categories.internal-categories.internal'],
    ['read', 'categories.reappro.category'],
    ['add', 'categories.reappro.category'],
    ['edit', 'categories.reappro.category'],
    ['delete', 'categories.reappro.category'],
    ['read', 'categories.reappro.config'],
    ['edit', 'categories.reappro.config'],
    ['delete', 'categories.reappro.config'],
    ['export', 'categories.reappro.config'],
    ['import', 'categories.reappro.config'],
    ['export', 'categories.reappro.simulation'],
    ['import', 'categories.reappro.simulation'],
    ['launch-reappro-simulation', 'categories.reappro.simulation'],
    ['read', 'categories.reappro.adjustment'],
    ['edit', 'categories.reappro.adjustment'],
    ['delete', 'categories.reappro.adjustment'],
    ['read', 'categories.reappro.algorithm'],
    ['edit', 'categories.reappro.algorithm'],
    ['read', 'categories.brands.brand'],
    ['add', 'categories.brands.brand'],
    ['edit', 'categories.brands.brand'],
    ['delete', 'categories.brands.brand'],
  ],
  'products:read': [
    ['read', 'products.products.product'],
    ['export', 'products.products.product'],
  ],
  'products:edit': [
    ['read', 'products.products.product'],
    ['add', 'products.products.product'],
    ['edit', 'products.products.product'],
    ['delete', 'products.products.product'],
    ['export', 'products.products.product'],
    ['import', 'products.products.product'],
  ],
  'users:read': [
    ['read', 'users.app-users.user'],
    ['read', 'users.app-users.solvency'],
    ['read', 'users.payment-defaults.default'],
    ['read', 'users.bo-users.user'],
    ['read', 'users.bo-users.api-key'],
  ],
  'users:edit': [
    ['read', 'users.app-users.user'],
    ['edit', 'users.app-users.user'],
    ['delete', 'users.app-users.user'],
    ['edit', 'users.app-users.ban'],
    ['send-ban-email', 'users.app-users.ban'],
    ['read', 'users.app-users.solvency'],
    ['read', 'users.payment-defaults.default'],
    ['read', 'users.bo-users.user'],
    ['add', 'users.bo-users.user'],
    ['edit', 'users.bo-users.user'],
    ['delete', 'users.bo-users.user'],
    ['reset-password', 'users.bo-users.user'],
    ['edit-legacy-rights', 'users.bo-users.user'],
    ['read', 'users.bo-users.api-key'],
    ['add', 'users.bo-users.api-key'],
    ['edit', 'users.bo-users.api-key'],
    ['delete', 'users.bo-users.api-key'],
    ['issue-for-others', 'users.bo-users.api-key'],
  ],
  'promos:read': [
    ['read', 'promo.temporary-campaigns.campaign'],
    ['export', 'promo.temporary-campaigns.single-use-codes'],
  ],
  'promos:edit': [
    ['read', 'promo.temporary-campaigns.campaign'],
    ['add', 'promo.temporary-campaigns.campaign'],
    ['edit', 'promo.temporary-campaigns.campaign'],
    ['delete', 'promo.temporary-campaigns.campaign'],
    ['add', 'promo.temporary-campaigns.single-use-codes'],
    ['export', 'promo.temporary-campaigns.single-use-codes'],
  ],
  'work-concil:read': [
    ['read', 'works-council.works-council.partnership'],
    ['export', 'accounting.accounting.works-council-history'],
  ],
  'work-concil:edit': [
    ['read', 'works-council.works-council.partnership'],
    ['add', 'works-council.works-council.partnership'],
    ['edit', 'works-council.works-council.partnership'],
    ['delete', 'works-council.works-council.partnership'],
    ['export', 'accounting.accounting.works-council-history'],
  ],
  'notifications:read': [['read', 'notifications.notifications.notification']],
  'notifications:edit': [
    ['read', 'notifications.notifications.notification'],
    ['add', 'notifications.notifications.notification'],
    ['add', 'notifications.notifications.duplication'],
  ],
  'stores:read': [
    ['read', 'stores.store-list.store'],
    ['export', 'stores.store-list.store'],
    ['read', 'stores.store-stocks.stock'],
    ['export', 'stores.store-stocks.stock'],
    ['read', 'stores.current-inventory.inventory'],
    ['read', 'stores.all-inventories.history'],
    ['export', 'stores.all-inventories.history'],
    ['read', 'stores.schedules.schedule'],
    ['read', 'stores.kiosks.kiosk'],
    ['read', 'stores.statistics.statistics'],
    ['export', 'stores.statistics.statistics'],
    ['read', 'network.network.network-dashboard'],
    ['export', 'network.network.network-dashboard'],
    ['read', 'cameras.cameras.config'],
  ],
  'stores:edit': [
    ['read', 'stores.store-list.store'],
    ['add', 'stores.store-list.store'],
    ['edit', 'stores.store-list.store'],
    ['delete', 'stores.store-list.store'],
    ['export', 'stores.store-list.store'],
    ['read', 'stores.store-stocks.stock'],
    ['export', 'stores.store-stocks.stock'],
    ['read', 'stores.current-inventory.inventory'],
    ['edit', 'stores.current-inventory.inventory'],
    ['delete', 'stores.current-inventory.inventory'],
    ['validate-inventory', 'stores.current-inventory.validation'],
    ['read', 'stores.all-inventories.history'],
    ['edit', 'stores.all-inventories.history'],
    ['export', 'stores.all-inventories.history'],
    ['read', 'stores.schedules.schedule'],
    ['edit', 'stores.schedules.schedule'],
    ['sync-schedule', 'stores.schedules.schedule'],
    ['read', 'stores.kiosks.kiosk'],
    ['edit', 'stores.kiosks.kiosk'],
    ['restart-kiosk', 'stores.kiosks.kiosk'],
    ['open-door', 'stores.kiosks.kiosk'],
    ['read', 'stores.statistics.statistics'],
    ['export', 'stores.statistics.statistics'],
    ['read', 'network.network.network-dashboard'],
    ['export', 'network.network.network-dashboard'],
    ['read', 'cameras.cameras.config'],
    ['add', 'cameras.cameras.config'],
    ['edit', 'cameras.cameras.config'],
    ['delete', 'cameras.cameras.config'],
    ['restart-camera-runtime', 'cameras.cameras.config'],
    ['sync-cameras', 'cameras.cameras.config'],
  ],
  'accounting:read': [
    ['read', 'accounting.accounting.figures'],
    ['export', 'accounting.accounting.figures'],
    ['export', 'accounting.accounting.balance'],
    ['export', 'accounting.accounting.payments'],
    ['read', 'accounting.accounting.warnings'],
  ],
  'accounting:edit': [
    ['read', 'accounting.accounting.figures'],
    ['export', 'accounting.accounting.figures'],
    ['export', 'accounting.accounting.balance'],
    ['export', 'accounting.accounting.payments'],
    ['read', 'accounting.accounting.warnings'],
  ],
  'quality:read': [
    ['read', 'quality.search-by-lot.lot-traceability'],
    ['read', 'quality.search-by-article.article-traceability'],
  ],
  'quality:edit': [
    ['read', 'quality.search-by-lot.lot-traceability'],
    ['edit', 'quality.search-by-lot.lot-traceability'],
    ['block-lot', 'quality.search-by-lot.lot-traceability'],
    ['read', 'quality.search-by-article.article-traceability'],
  ],
  'cart-control:read': [['read', 'cart-control.cart-control.live-control']],
  'cart-control:edit': [
    ['read', 'cart-control.cart-control.live-control'],
    ['enter-support-mode', 'cart-control.cart-control.support-mode'],
  ],
  'daily-sales:read': [
    ['read', 'daily-sales.daily-sales.daily-sales'],
    ['export', 'daily-sales.daily-sales.daily-sales'],
    ['read', 'daily-sales.daily-sales.auto-calculation'],
  ],
  'daily-sales:edit': [
    ['read', 'daily-sales.daily-sales.daily-sales'],
    ['edit', 'daily-sales.daily-sales.daily-sales'],
    ['export', 'daily-sales.daily-sales.daily-sales'],
    ['import', 'daily-sales.daily-sales.daily-sales'],
    ['read', 'daily-sales.daily-sales.auto-calculation'],
    ['edit', 'daily-sales.daily-sales.auto-calculation'],
    ['launch-sales-calculation', 'daily-sales.daily-sales.auto-calculation'],
  ],
  'sellers:read': [
    ['read', 'sellers.sellers.seller'],
    ['read', 'sellers.establishments.establishment'],
  ],
  'sellers:edit': [
    ['read', 'sellers.sellers.seller'],
    ['add', 'sellers.sellers.seller'],
    ['edit', 'sellers.sellers.seller'],
    ['delete', 'sellers.sellers.seller'],
    ['read', 'sellers.establishments.establishment'],
    ['add', 'sellers.establishments.establishment'],
    ['edit', 'sellers.establishments.establishment'],
    ['delete', 'sellers.establishments.establishment'],
  ],
  'cameras:read': [
    ['read', 'cameras.cameras.stream'],
    ['read', 'pre-authorizations.pre-authorizations.session'],
  ],
  'cameras:edit': [
    ['read', 'cameras.cameras.stream'],
    ['add', 'cameras.cameras.stream'],
    ['edit', 'cameras.cameras.stream'],
    ['delete', 'cameras.cameras.stream'],
    ['start-intervention', 'cameras.cameras.stream'],
    ['read', 'pre-authorizations.pre-authorizations.session'],
    ['edit', 'pre-authorizations.pre-authorizations.session'],
    ['release-pre-authorization', 'pre-authorizations.pre-authorizations.session'],
  ],
  'camera-views:read': [['read', 'cameras.cameras.stream']],
  'camera-views:edit': [
    ['read', 'cameras.cameras.stream'],
    ['add', 'cameras.cameras.stream'],
    ['edit', 'cameras.cameras.stream'],
    ['delete', 'cameras.cameras.stream'],
  ],
  'sensors:read': [
    ['read', 'sensors.sensors.sensor'],
    ['read', 'sensors.fridges.fridge'],
  ],
  'sensors:edit': [
    ['read', 'sensors.sensors.sensor'],
    ['add', 'sensors.sensors.sensor'],
    ['edit', 'sensors.sensors.sensor'],
    ['delete', 'sensors.sensors.sensor'],
    ['read', 'sensors.fridges.fridge'],
    ['add', 'sensors.fridges.fridge'],
    ['edit', 'sensors.fridges.fridge'],
    ['delete', 'sensors.fridges.fridge'],
  ],
  'hr-planning:read': [
    ['read', 'hr.planning.weekly-planning'],
    ['export', 'hr.planning.weekly-planning'],
    ['read', 'hr.planning.settings'],
    ['read', 'hr.dashboard.overview'],
    ['export', 'hr.dashboard.overview'],
    ['read', 'hr.staff.collaborator'],
    ['export', 'hr.staff.collaborator'],
    ['read', 'hr.staff.identity'],
    ['read', 'hr.staff.position'],
    ['read', 'hr.staff.assignments'],
    ['read', 'hr.staff.contract-terms'],
    ['read', 'hr.staff.cost'],
    ['read', 'hr.staff.offer'],
    ['read', 'hr.staff.comment'],
    ['read', 'hr.staff.contract'],
    ['read', 'hr.staff.formation'],
    ['read', 'hr.staff.availability'],
    ['read', 'hr.staff.goods'],
    ['read', 'hr.clocking.clocking'],
    ['export', 'hr.clocking.clocking'],
    ['read', 'hr.absences.absence'],
    ['export', 'hr.absences.absence'],
    ['read', 'sms.sms.sent'],
    ['export', 'sms.sms.sent'],
    ['read', 'interim-agencies.interim-agencies.agency'],
  ],
  'hr-planning:edit': [
    ['read', 'hr.planning.weekly-planning'],
    ['add', 'hr.planning.weekly-planning'],
    ['edit', 'hr.planning.weekly-planning'],
    ['delete', 'hr.planning.weekly-planning'],
    ['export', 'hr.planning.weekly-planning'],
    ['publish-planning', 'hr.planning.weekly-planning'],
    ['read', 'hr.planning.settings'],
    ['edit', 'hr.planning.settings'],
    ['read', 'hr.dashboard.overview'],
    ['export', 'hr.dashboard.overview'],
    ['read', 'hr.staff.collaborator'],
    ['add', 'hr.staff.collaborator'],
    ['delete', 'hr.staff.collaborator'],
    ['export', 'hr.staff.collaborator'],
    ['import', 'hr.staff.collaborator'],
    ['read', 'hr.staff.identity'],
    ['edit', 'hr.staff.identity'],
    ['read', 'hr.staff.position'],
    ['edit', 'hr.staff.position'],
    ['read', 'hr.staff.assignments'],
    ['edit', 'hr.staff.assignments'],
    ['read', 'hr.staff.contract-terms'],
    ['edit', 'hr.staff.contract-terms'],
    ['read', 'hr.staff.cost'],
    ['edit', 'hr.staff.cost'],
    ['read', 'hr.staff.offer'],
    ['edit', 'hr.staff.offer'],
    ['read', 'hr.staff.comment'],
    ['edit', 'hr.staff.comment'],
    ['read', 'hr.staff.contract'],
    ['add', 'hr.staff.contract'],
    ['edit', 'hr.staff.contract'],
    ['read', 'hr.staff.formation'],
    ['add', 'hr.staff.formation'],
    ['edit', 'hr.staff.formation'],
    ['delete', 'hr.staff.formation'],
    ['read', 'hr.staff.availability'],
    ['edit', 'hr.staff.availability'],
    ['read', 'hr.staff.goods'],
    ['add', 'hr.staff.goods'],
    ['edit', 'hr.staff.goods'],
    ['delete', 'hr.staff.goods'],
    ['read', 'hr.clocking.clocking'],
    ['add', 'hr.clocking.clocking'],
    ['edit', 'hr.clocking.clocking'],
    ['export', 'hr.clocking.clocking'],
    ['validate-clocking', 'hr.clocking.clocking'],
    ['reopen-clocking', 'hr.clocking.clocking'],
    ['add-manual-clocking', 'hr.clocking.clocking'],
    ['cancel-clocking', 'hr.clocking.clocking'],
    ['correct-outside-window', 'hr.clocking.clocking'],
    ['read', 'hr.absences.absence'],
    ['add', 'hr.absences.absence'],
    ['edit', 'hr.absences.absence'],
    ['delete', 'hr.absences.absence'],
    ['export', 'hr.absences.absence'],
    ['read', 'sms.sms.sent'],
    ['export', 'sms.sms.sent'],
    ['resend-unread-sms', 'sms.sms.sent'],
    ['read', 'interim-agencies.interim-agencies.agency'],
    ['add', 'interim-agencies.interim-agencies.agency'],
    ['edit', 'interim-agencies.interim-agencies.agency'],
    ['delete', 'interim-agencies.interim-agencies.agency'],
  ],
  'real-estate:read': [
    ['read', 'real-estate.map.map'],
    ['export', 'real-estate.map.map'],
    ['read', 'real-estate.kanban.project'],
    ['export', 'real-estate.kanban.project'],
    ['read', 'real-estate.tasks.task'],
    ['read', 'real-estate.calendar.calendar'],
    ['read', 'real-estate.contacts.contact'],
    ['read', 'real-estate.areas-of-interest.area'],
    ['read', 'real-estate.kanban.document'],
    ['read', 'real-estate.map.pre-project'],
  ],
  'real-estate:edit': [
    ['read', 'real-estate.map.map'],
    ['add', 'real-estate.map.map'],
    ['edit', 'real-estate.map.map'],
    ['delete', 'real-estate.map.map'],
    ['export', 'real-estate.map.map'],
    ['import', 'real-estate.map.map'],
    ['read', 'real-estate.kanban.project'],
    ['add', 'real-estate.kanban.project'],
    ['edit', 'real-estate.kanban.project'],
    ['delete', 'real-estate.kanban.project'],
    ['export', 'real-estate.kanban.project'],
    ['edit', 'real-estate.kanban.archive'],
    ['archive-real-estate-project', 'real-estate.kanban.archive'],
    ['read', 'real-estate.tasks.task'],
    ['add', 'real-estate.tasks.task'],
    ['edit', 'real-estate.tasks.task'],
    ['delete', 'real-estate.tasks.task'],
    ['read', 'real-estate.calendar.calendar'],
    ['add', 'real-estate.calendar.calendar'],
    ['edit', 'real-estate.calendar.calendar'],
    ['delete', 'real-estate.calendar.calendar'],
    ['read', 'real-estate.contacts.contact'],
    ['add', 'real-estate.contacts.contact'],
    ['edit', 'real-estate.contacts.contact'],
    ['delete', 'real-estate.contacts.contact'],
    ['read', 'real-estate.areas-of-interest.area'],
    ['add', 'real-estate.areas-of-interest.area'],
    ['edit', 'real-estate.areas-of-interest.area'],
    ['delete', 'real-estate.areas-of-interest.area'],
    ['read', 'real-estate.kanban.document'],
    ['add', 'real-estate.kanban.document'],
    ['edit', 'real-estate.kanban.document'],
    ['delete', 'real-estate.kanban.document'],
    ['read', 'real-estate.map.pre-project'],
    ['edit', 'real-estate.map.pre-project'],
    ['delete', 'real-estate.map.pre-project'],
    ['import', 'real-estate.map.pre-project'],
  ],
  'roles:read': [['read', 'roles-admin.roles.role']],
  'roles:edit': [
    ['read', 'roles-admin.roles.role'],
    ['add', 'roles-admin.roles.role'],
    ['edit', 'roles-admin.roles.role'],
    ['edit', 'roles-admin.roles.admin-assignment'],
    ['assign-bo-role', 'users.bo-users.user'],
  ],
  'platform-stocks:read': [
    ['read', 'platform-stock.platform-stock.stock'],
    ['export', 'platform-stock.platform-stock.stock'],
  ],
  'platform-stocks:edit': [
    ['read', 'platform-stock.platform-stock.stock'],
    ['add', 'platform-stock.platform-stock.stock'],
    ['edit', 'platform-stock.platform-stock.stock'],
    ['delete', 'platform-stock.platform-stock.stock'],
    ['export', 'platform-stock.platform-stock.stock'],
    ['import', 'platform-stock.platform-stock.stock'],
  ],
  'visio-reviews:read': [
    ['read', 'visio.yesterday-interactions.interaction'],
    ['read', 'visio.exchanges-to-evaluate.exchange'],
    ['read', 'visio.overview.overview'],
    ['read', 'visio.administration.administration'],
  ],
  'visio-reviews:edit': [
    ['read', 'visio.yesterday-interactions.interaction'],
    ['read', 'visio.exchanges-to-evaluate.exchange'],
    ['edit', 'visio.exchanges-to-evaluate.evaluation'],
    ['read', 'visio.overview.overview'],
    ['read', 'visio.administration.administration'],
    ['edit', 'visio.administration.administration'],
  ],
  'prices:read': [['read', 'products.products.product']],
  'prices:edit': [
    ['read', 'products.products.product'],
    ['edit-prices', 'products.products.product'],
  ],
} as const satisfies Partial<Record<LegacyV1PermissionKey, readonly LegacyV2Tuple[]>>

/** Couples v1 couverts par la table, dans l'ordre de déclaration. */
export const LEGACY_V1_PERMISSION_KEYS = Object.keys(
  LEGACY_V1_TO_V2_GRANTS,
) as (keyof typeof LEGACY_V1_TO_V2_GRANTS)[]

/** Un couple v1 inconnu de la table ne produit rien (bord de lecture des données existantes). */
export const legacyV1Grants = (resource: string, permissionType: string): LegacyV2Tuple[] => {
  const entry = (LEGACY_V1_TO_V2_GRANTS as Record<string, readonly LegacyV2Tuple[] | undefined>)[
    `${resource}:${permissionType}`
  ]
  return entry === undefined ? [] : [...entry]
}

/** Toute la matrice v2, telle que la reçoit le rôle `Super Admin`. */
export const allRbacV2Grants = (): LegacyV2Tuple[] =>
  Object.entries(rbacV2Abilities).flatMap(([resource, actions]) =>
    (actions as readonly RbacV2Action[]).map(
      (action) => [action, resource as RbacV2Resource] as LegacyV2Tuple,
    ),
  )

/**
 * Périmètre à écrire pour un tuple converti. `null` (= `global` à la lecture, cf. `storedGrant.ts`)
 * partout, sauf les tuples foncier déclarés à périmètre quand la permission v1 d'origine portait
 * `real-estate-restricted` : ceux-là seuls conservent le « Prez projet » du compte.
 */
export const legacyV1ScopeForV2Tuple = (
  tuple: LegacyV2Tuple,
  legacyScope: string | null,
): RbacV2Scope | null => {
  if (legacyScope !== 'real-estate-restricted') return null
  const entry = (
    RBAC_V2_SCOPED_PERMISSIONS as Record<string, { actions: readonly string[] } | undefined>
  )[tuple[1]]
  if (entry === undefined || !entry.actions.includes(tuple[0])) return null
  return 'real-estate-restricted'
}

/** Ligne v2 à insérer pour un rôle historique, prête pour `role_permissions`. */
export interface LegacyConvertedPermission {
  resource: RbacV2Resource
  action: RbacV2Action
  scope: RbacV2Scope | null
}

const permissionKey = (permission: LegacyConvertedPermission) =>
  `${permission.resource}|${permission.action}`

/**
 * Convertit l'ensemble des permissions v1 d'un rôle en permissions v2, dédoublonnées sur
 * `(resource, action)` — la clé d'unicité de `role_permissions`. Deux couples v1 peuvent produire
 * le même tuple (`cameras`/`camera-views` sur le flux, `products`/`prices` sur la fiche produit) :
 * la première occurrence gagne, et un tuple obtenu à la fois en global et en « Prez projet » reste
 * global (le périmètre le plus large l'emporte, jamais l'inverse).
 */
export const convertLegacyPermissions = (
  legacy: readonly { resource: string; permissionType: string; scope: string | null }[],
): LegacyConvertedPermission[] => {
  const byKey = new Map<string, LegacyConvertedPermission>()
  for (const row of legacy) {
    for (const tuple of legacyV1Grants(row.resource, row.permissionType)) {
      const converted: LegacyConvertedPermission = {
        resource: tuple[1],
        action: tuple[0],
        scope: legacyV1ScopeForV2Tuple(tuple, row.scope),
      }
      const key = permissionKey(converted)
      const existing = byKey.get(key)
      if (existing === undefined) byKey.set(key, converted)
      else if (existing.scope !== null && converted.scope === null) byKey.set(key, converted)
    }
  }
  return [...byKey.values()]
}

/** Permissions v2 du rôle `Super Admin` : toute la matrice, sans périmètre. */
export const fullAccessPermissions = (): LegacyConvertedPermission[] =>
  allRbacV2Grants().map(([action, resource]) => ({ resource, action, scope: null }))
